import _ from 'lodash';
import permissionService from 'services/permission.service';
import { IMenuItemConfig } from 'containers/App/navConfig';
import { PermissionFlagName } from 'lib/types';

export interface INavContext {
    user: any;
    isPhone: boolean;
    additionalTabName: string;
}

export interface IResolvedNavResult {
    activeKey?: string;
    activeLabel?: string;
    matchedPath?: string;
    hasVisibleMatch: boolean;
    hasAnyMatch: boolean;
}

export const normalizePath = (path: string) => path.replace(/\/$/, '');

export const getNavLabel = (config: IMenuItemConfig, additionalTabName: string) => (
    typeof config.label === 'function' ? config.label(additionalTabName) : config.label
);

export const hasNavPermission = (permissionKey: string | PermissionFlagName, user: any): boolean => {
    if (permissionKey === 'additional-tab' && permissionService.haveAccessToView('additional-tab')) return true;
    if (permissionKey === 'account' && permissionService.haveAccessToView('account')) return true;
    if (permissionKey === 'settings' && permissionService.haveAccessToView('settings')) return true;

    if (permissionService.haveAccessToView(permissionKey as any)) return true;

    if (user && user.permissions && user.permissions[permissionKey as keyof typeof user.permissions]) {
        return true;
    }

    return false;
};

export const isNavItemVisible = (item: IMenuItemConfig, context: INavContext) => {
    if (item.hideIf && item.hideIf(context.user)) return false;
    if (!hasNavPermission(item.permissionKey, context.user)) return false;
    if (item.requiresDesktop && context.isPhone) return false;
    return true;
};

export const hasVisibleChildren = (item: IMenuItemConfig, context: INavContext): boolean => {
    if (!item.subItems || item.subItems.length === 0) return false;

    return _.some(item.subItems, (child) => {
        if (!isNavItemVisible(child, context)) return false;
        if (child.subItems && child.subItems.length > 0) {
            return hasVisibleChildren(child, context);
        }
        return true;
    });
};

export const isNavItemVisibleWithChildren = (item: IMenuItemConfig, context: INavContext) => {
    if (!isNavItemVisible(item, context)) return false;
    if (item.subItems && item.subItems.length > 0 && !hasVisibleChildren(item, context)) return false;
    return true;
};

export const getVisibleSubItems = (item: IMenuItemConfig, context: INavContext): IMenuItemConfig[] => {
    if (!item.subItems || item.subItems.length === 0) return [];

    return _.filter(item.subItems, (child) => {
        if (!isNavItemVisible(child, context)) return false;
        if (child.subItems && child.subItems.length > 0) {
            return hasVisibleChildren(child, context);
        }
        return true;
    });
};

export const isPathMatch = (currentPath: string, targetPath: string, publicUrl: string = '') => {
    const current = normalizePath(currentPath);
    const target = normalizePath(`${publicUrl}${targetPath}`);

    if (current === target) return true;

    if (target !== `${publicUrl}/orders` && current.startsWith(`${target}/`)) {
        return true;
    }

    return false;
};

export interface FlattenedNavEntry {
    item: IMenuItemConfig;
    topLevelId: string;
    label: string;
    path: string;
    depth: number;
    pathLength: number;
}

export const flattenNavItems = (
    items: IMenuItemConfig[],
    context: INavContext,
    publicUrl: string = '',
    topLevelId?: string,
    depth: number = 0
): FlattenedNavEntry[] => _.flatMap(items, (item) => {
    const currentTopId = topLevelId ?? item.id;
    const entry: FlattenedNavEntry = {
        item,
        topLevelId: currentTopId,
        label: getNavLabel(item, context.additionalTabName),
        path: item.path,
        depth,
        pathLength: normalizePath(`${publicUrl}${item.path}`).length
    };

    if (!item.subItems || item.subItems.length === 0) {
        return [entry];
    }

    return [entry, ...flattenNavItems(item.subItems, context, publicUrl, currentTopId, depth + 1)];
});

export const resolveActiveNav = (
    items: IMenuItemConfig[],
    context: INavContext,
    currentPath: string,
    publicUrl: string = ''
): IResolvedNavResult => {
    const flattened = flattenNavItems(items, context, publicUrl);

    const visibleMatches = _.filter(flattened, (entry) => {
        if (!entry.path || entry.path === '#') return false;
        if (!isNavItemVisibleWithChildren(entry.item, context)) return false;
        return isPathMatch(currentPath, entry.path, publicUrl);
    });

    if (visibleMatches.length > 0) {
        const bestMatch = _.maxBy(visibleMatches, (entry) => entry.pathLength * 10 + entry.depth);
        return {
            activeKey: bestMatch?.topLevelId,
            activeLabel: bestMatch?.label,
            matchedPath: bestMatch?.path,
            hasVisibleMatch: true,
            hasAnyMatch: true
        };
    }

    const hasAnyMatch = _.some(flattened, (entry) => entry.path && entry.path !== '#' && isPathMatch(currentPath, entry.path, publicUrl));

    return {
        hasVisibleMatch: false,
        hasAnyMatch
    };
};
